import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Button from '../../shared/TailwindComponents/Button';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import Badge from '../../shared/TailwindComponents/Badge';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import Flag from '../../shared/TailwindComponents/Flag';
import {
  PiPencilLight,
  PiCalendarBlankLight,
  PiClockCountdownLight,
  PiFlagLight,
  PiCircleDashedLight,
  PiSuitcaseSimpleLight,
  PiArrowSquareOutLight,
  PiCalendarCheckLight,
  PiClockClockwiseLight,
  PiUserLight,
  PiTextAlignLeftLight,
} from 'react-icons/pi';
import { formatDate, formatDateTime, getStatusColor } from '../../../utils/functions';
import { getJurisdictionDisplay } from '../../Matters/Matters';
import RecentActivityCard from '../../shared/RecentActivityCard';
import { fetchTaskAuditLog } from '../../../store/actions/TasksActions';
import { useAppDispatch } from '../../../store';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, taskDetailsTabTourSteps } from '../../shared/FeatureTour';

interface TaskDetailsTabContentProps {
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  formik: any;
  hasChanges: boolean;
  setHasChanges: (changes: boolean) => void;
  onSubmit: () => void;
  taskStatus: string;
  matterData: any;
  setMatterData: React.Dispatch<React.SetStateAction<any>>;
  matterLoading: boolean;
  selectedCaseId: string;
  setSelectedCaseId: React.Dispatch<React.SetStateAction<string>>;
  matters: any[];
  handleChangeMatter: () => Promise<void>;
  onOpenAuditLog?: () => void;
}

const TaskDetailsTabContent: React.FC<TaskDetailsTabContentProps> = ({
  taskDetails,
  taskUuid,
  userAccessLevel,
  isEditing,
  setIsEditing,
  formik,
  hasChanges,
  setHasChanges,
  onSubmit,
  taskStatus,
  matterData,
  setMatterData,
  matterLoading,
  selectedCaseId,
  setSelectedCaseId,
  matters,
  handleChangeMatter,
  onOpenAuditLog,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-details-tab',
    steps: taskDetailsTabTourSteps,
    readyToShow: !!taskDetails,
    autoStartDelay: 600,
  });

  const handleFormChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    setHasChanges(true);
  };

  const priorityOptions: DropdownOption[] = useMemo(() => [
    { value: 'low', label: t('Low') },
    { value: 'medium', label: t('Medium') },
    { value: 'high', label: t('High') },
  ], [t]);

  const matterOptions: DropdownOption[] = useMemo(() => matters.map((matter: any) => ({
    value: matter.uuid || matter.id || matter.MatterID,
    label: matter.title || matter.MatterName || `${t('Matter')} ${matter.id || matter.MatterID}`,
  })), [matters, t]);

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'amber';
      case 'critical': return 'red';
      default: return 'yellow';
    }
  };

  const dueDateInfo = useMemo(() => {
    if (!formik.values.due_date || taskStatus === 'completed') return null;
    const now = new Date();
    const due = new Date(formik.values.due_date);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    if (diffMs < 0) {
      return { text: t('Overdue'), urgency: 'overdue' as const };
    } else if (diffHours <= 48) {
      return { text: t('Due Soon'), urgency: 'soon' as const };
    } else if (diffDays <= 7) {
      return { text: t('This Week'), urgency: 'upcoming' as const };
    }
    return null;
  }, [formik.values.due_date, taskStatus, t]);

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return t('Completed');
      case 'in_progress': return t('In Progress');
      case 'todo': return t('To Do');
      case 'archived': return t('Archived');
      case 'deleted': return t('Deleted');
      default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : t('To Do');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'todo': return 'gray';
      case 'archived': case 'deleted': return 'red';
      default: return 'gray';
    }
  };

  const createdByName = useMemo(() => {
    if (!taskDetails?.collaborators?.length) return null;
    const creator = taskDetails.collaborators.find((c: any) => c.role === 'creator');
    return creator?.name || null;
  }, [taskDetails?.collaborators]);

  return (
    <>
    <div className="max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar-main px-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Left Column ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* ── Description Card ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm" data-tour-id="task-description-card">
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <PiTextAlignLeftLight className="h-4 w-4" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('Description')}</span>
              </div>
              {!isEditing && userAccessLevel !== 'view' && (taskStatus !== 'completed' && taskStatus !== 'deleted') && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant='primary'
                  size='small'
                  icon={<PiPencilLight className="h-3.5 w-3.5" />}
                >
                  {t('Edit')}
                </Button>
              )}
            </div>

            {isEditing && userAccessLevel !== 'view' ? (
              <div className="p-5 space-y-4">
                <Textarea
                  label={t('Title')}
                  placeholder={t('Enter task title')}
                  value={formik.values.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  error={formik.touched.title && formik.errors.title as string}
                  className="w-full"
                  id="title"
                />
                <Textarea
                  label={t('Description')}
                  placeholder={t('Enter task description')}
                  value={formik.values.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  error={formik.touched.description && formik.errors.description as string}
                  className="w-full"
                  id="description"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DateTimePicker
                    id="due_date"
                    label={t('Due Date')}
                    selected={formik.values.due_date ? new Date(formik.values.due_date) : null}
                    onChange={(date: Date | null) => handleFormChange('due_date', date ? date.toISOString() : '')}
                    placeholder={t('Select due date')}
                    dateFormat="MMM d, yyyy h:mm aa"
                    showTimeSelect={true}
                    timeIntervals={15}
                    required={false}
                    error={formik.touched.due_date && !!formik.errors.due_date}
                    helperText={formik.touched.due_date && formik.errors.due_date as string}
                    fullWidth={true}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('Priority')}
                    </label>
                    <Dropdown
                      options={priorityOptions}
                      value={formik.values.priority}
                      onSelect={(option) => handleFormChange('priority', option.value)}
                      placeholder={t('Select Priority')}
                      className="border-gray-300 focus:ring-primary-500"
                      fullWidth={true}
                    />
                    {formik.touched.priority && formik.errors.priority && (
                      <p className="text-xs text-red-500 mt-1">{formik.errors.priority as string}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                  <Button
                    variant="neutral"
                    size="small"
                    onClick={() => {
                      setIsEditing(false);
                      setHasChanges(false);
                      formik.resetForm();
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => formik.handleSubmit()}
                    disabled={!hasChanges}
                  >
                    {t('Save Changes')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-5">
                {formik.values.description ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                    {formik.values.description}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                    {t('No description provided')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── Metadata Grid ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-tour-id="task-metadata-grid">

            {/* Status */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Status')}</span>
              </div>
              <Badge color={getStatusBadgeColor(taskStatus) as any}>
                {getStatusLabel(taskStatus)}
              </Badge>
            </div>

            {/* Priority */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiFlagLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Priority')}</span>
              </div>
              <Badge color={getPriorityBadgeColor(formik.values.priority) as any}>
                {formik.values.priority ? formik.values.priority.charAt(0).toUpperCase() + formik.values.priority.slice(1) : t('Medium')}
              </Badge>
            </div>

            {/* Created */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiCalendarBlankLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {taskDetails?.created_at ? formatDate(taskDetails.created_at) : '-'}
              </p>
            </div>

            {/* Due Date */}
            <div className={clsx(
              'border rounded-lg shadow-sm p-4',
              dueDateInfo?.urgency === 'overdue'
                ? 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10'
                : dueDateInfo?.urgency === 'soon'
                ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
            )}>
              <div className="flex items-center gap-1.5 mb-2">
                <PiClockCountdownLight className={clsx(
                  'h-3.5 w-3.5',
                  dueDateInfo?.urgency === 'overdue' ? 'text-red-400' :
                  dueDateInfo?.urgency === 'soon' ? 'text-amber-400' :
                  'text-gray-400 dark:text-gray-500',
                )} />
                <span className={clsx(
                  'text-[11px] font-medium',
                  dueDateInfo?.urgency === 'overdue' ? 'text-red-400' :
                  dueDateInfo?.urgency === 'soon' ? 'text-amber-400' :
                  'text-gray-400 dark:text-gray-500',
                )}>{t('Due Date')}</span>
              </div>
              <div className="flex flex-col gap-1">
                <p className={clsx(
                  'text-sm font-medium',
                  dueDateInfo?.urgency === 'overdue' ? 'text-red-600 dark:text-red-400' :
                  dueDateInfo?.urgency === 'soon' ? 'text-amber-600 dark:text-amber-400' :
                  'text-gray-700 dark:text-gray-200',
                )}>
                  {formik.values.due_date ? formatDate(formik.values.due_date) : t('Not set')}
                </p>
                {dueDateInfo && (
                  <span className={clsx(
                    'text-[11px] font-medium',
                    dueDateInfo.urgency === 'overdue' && 'text-red-500 dark:text-red-400',
                    dueDateInfo.urgency === 'soon' && 'text-amber-500 dark:text-amber-400',
                    dueDateInfo.urgency === 'upcoming' && 'text-yellow-600 dark:text-yellow-400',
                  )}>
                    {dueDateInfo.text}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Activity Row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {createdByName && (
              <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                <PiUserLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created By')}</span>
                  <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{createdByName}</p>
                </div>
              </div>
            )}

            {taskDetails?.updated_at && (
              <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                <PiClockClockwiseLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Last Updated')}</span>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(taskDetails.updated_at)}</p>
                </div>
              </div>
            )}

            {taskDetails?.completed_at && (
              <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                <PiCalendarCheckLight className="h-4 w-4 text-green-400 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Completed')}</span>
                  <p className="text-sm text-green-600 dark:text-green-400">{formatDateTime(taskDetails.completed_at)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-5">

          {/* ── Linked Matter Card ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm" data-tour-id="task-linked-matter">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
              <PiSuitcaseSimpleLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{t('Linked Matter')}</span>
            </div>

            <div className="p-4 space-y-3">
              {taskDetails?.matter?.uuid ? (
                <>
                  {(() => {
                    const matter: any = taskDetails.matter;
                    const matterTitle = matter.title || taskDetails.matter_name || t('Untitled Matter');
                    const jurisdictionData = getJurisdictionDisplay(matter.jurisdiction);
                    const statusName = matter.status || matter.status_name;
                    const priorityConfig: Record<string, { dot: string; label: string }> = {
                      low: { dot: 'bg-gray-400', label: t('Low') },
                      medium: { dot: 'bg-yellow-400', label: t('Medium') },
                      high: { dot: 'bg-orange-400', label: t('High') },
                      urgent: { dot: 'bg-red-500', label: t('Urgent') },
                    };
                    const prio = matter.priority ? priorityConfig[matter.priority] : null;
                    return (
                      <button
                        onClick={() => history.push(`/matters/${matter.uuid}`)}
                        className="group relative w-full text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all flex flex-col gap-3"
                      >
                        <div className="flex items-start gap-2.5">
                          <PiSuitcaseSimpleLight className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                          <h3 className="flex-1 text-sm text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
                            {matterTitle}
                          </h3>
                          <PiArrowSquareOutLight className="flex-shrink-0 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                        </div>

                        {matter.client?.name && (
                          <div className="flex items-center gap-1.5 -mt-1">
                            <PiUserLight className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{matter.client.name}</p>
                          </div>
                        )}

                        {jurisdictionData && (
                          <div className="flex items-center gap-1.5 -mt-1">
                            <Flag countryCode={jurisdictionData.countryCode} code={jurisdictionData.code} size="sm" className="rounded" />
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{matter.jurisdiction}</p>
                          </div>
                        )}

                        {(statusName || matter.category_name || matter.type || prio) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {statusName && (
                              <Badge color={getStatusColor(statusName) as any || 'gray'} className="capitalize">
                                {statusName}
                              </Badge>
                            )}
                            {matter.category_name && (
                              <Badge color="gray">{matter.category_name}</Badge>
                            )}
                            {matter.type && !matter.category_name && (
                              <Badge color="gray">{matter.type}</Badge>
                            )}
                            {prio && (
                              <div className="flex items-center gap-1">
                                <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                                <span className="text-[11px] text-gray-400 dark:text-gray-500">{prio.label}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {matter.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
                            {matter.description}
                          </p>
                        )}

                        {(matter.due_date || matter.updated_at) && (
                          <div className="mt-auto flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700/60 text-xs text-gray-400 dark:text-gray-500">
                            {matter.due_date && (
                              <div className="flex items-center gap-1">
                                <PiCalendarBlankLight className="w-3.5 h-3.5" />
                                <span>{t('Due')} {formatDate(matter.due_date)}</span>
                              </div>
                            )}
                            {matter.updated_at && (
                              <div className="flex items-center gap-1 ml-auto">
                                <PiClockClockwiseLight className="w-3.5 h-3.5" />
                                <span>{formatDateTime(matter.updated_at)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })()}

                  {userAccessLevel !== 'view' && (taskStatus !== 'completed' && taskStatus !== 'deleted') && (
                    <div className="space-y-2">
                      <Dropdown
                        options={matterOptions}
                        placeholder={t('Change matter...')}
                        onSelect={(option) => setSelectedCaseId(option.value)}
                        className="border-gray-300 focus:ring-primary-500"
                        fullWidth={true}
                      />
                      {selectedCaseId && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleChangeMatter}
                          disabled={matterLoading}
                          className="w-full"
                        >
                          {matterLoading ? t('Updating...') : t('Update Matter')}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-center py-4">
                    <PiSuitcaseSimpleLight className="h-8 w-8 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {t('No matter linked')}
                    </p>
                  </div>
                  {userAccessLevel !== 'view' && (taskStatus !== 'completed' && taskStatus !== 'deleted') && (
                    <div className="space-y-2">
                      <Dropdown
                        options={matterOptions}
                        placeholder={t('Link a matter...')}
                        onSelect={(option) => setSelectedCaseId(option.value)}
                        className="border-gray-300 focus:ring-primary-500"
                        fullWidth={true}
                      />
                      {selectedCaseId && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleChangeMatter}
                          disabled={matterLoading}
                          className="w-full"
                        >
                          {matterLoading ? t('Link Matter') : t('Linking...')}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Recent Activity ── */}
          <RecentActivityCard
            fetchEntries={async (limit) => {
              const res: any = await dispatch(fetchTaskAuditLog(taskUuid, 1, limit) as any);
              return res?.entries || [];
            }}
            onViewAll={onOpenAuditLog}
          />
        </div>
      </div>
    </div>
    <FeatureTour
      steps={taskDetailsTabTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TaskDetailsTabContent;
